import { Injectable } from '@angular/core';
import io from 'socket.io-client';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Tipo para el socket
type SocketType = ReturnType<typeof io>;

@Injectable({
	providedIn: 'root'
})
export class WebSocketService {
	private socket: SocketType | null = null;
	private connected = false;

	constructor() {}

	/**
	 * Conecta al servidor WebSocket
	 * @param token Token JWT para autenticación (opcional, se obtiene del localStorage si no se proporciona)
	 */
	connect(token?: string): void {
		if (this.connected) {
			console.warn('Ya existe una conexión activa al WebSocket');
			return;
		}

		// Para desarrollo, opcionalmente deshabilitar WebSocket
		/*if (!environment.production) {
			console.warn('🚧 WebSocket deshabilitado en modo desarrollo');
			return;
		}*/

		// Obtener token del localStorage si no se proporciona
		const authToken = token || localStorage.getItem('token') || '';

		this.socket = io(environment.WEBSOCKET_URL, {
			auth: {
				token: authToken
			},
			transports: ['websocket', 'polling'],
			reconnection: true,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
			reconnectionAttempts: 5
		});

		// Escuchar evento 'connected' del servidor
		this.socket.on('connected', (data) => {
			this.connected = true;
			console.log('✅ Conectado al WebSocket:', data);
		});

		// Escuchar evento 'connect' nativo de Socket.IO
		this.socket.on('connect', () => {
			this.connected = true;
			console.log('🔌 Socket.IO conectado');
		});

		this.socket.on('disconnect', () => {
			this.connected = false;
			console.log('❌ Desconectado del WebSocket');
		});

		this.socket.on('connect_error', (error) => {
			console.error('⚠️ Error de conexión al WebSocket:', error);
		});
	}

	/**
	 * Desconecta del servidor WebSocket
	 */
	disconnect(): void {
		if (this.socket) {
			this.socket.disconnect();
			this.socket = null;
			this.connected = false;
		}
	}

	/**
	 * Verifica si está conectado
	 */
	isConnected(): boolean {
		return (this.connected && this.socket?.connected) || false;
	}

	/**
	 * Escucha eventos del servidor
	 * @param eventName Nombre del evento a escuchar
	 * @returns Observable con los datos del evento
	 */
	listen<T>(eventName: string): Observable<T> {
		return new Observable((observer) => {
			const socket = this.socket;

			if (!socket) {
				observer.error(new Error('Socket no está conectado'));
				observer.complete();
				return undefined; // Retornar undefined explícitamente
			}

			socket.on(eventName, (data: T) => {
				observer.next(data);
			});

			// Cleanup cuando se desuscribe
			return () => {
				socket.off(eventName);
			};
		});
	}

	/**
	 * Emite un evento al servidor
	 * @param eventName Nombre del evento
	 * @param data Datos a enviar
	 */
	emit<T>(eventName: string, data?: T): void {
		if (!this.socket) {
			console.error('Socket no está conectado. No se puede emitir el evento:', eventName);
			return;
		}

		this.socket.emit(eventName, data);
	}

	/**
	 * Emite un evento y espera una respuesta
	 * @param eventName Nombre del evento
	 * @param data Datos a enviar
	 * @returns Observable con la respuesta
	 */
	emitWithAck<T, R>(eventName: string, data?: T): Observable<R> {
		return new Observable((observer) => {
			if (!this.socket) {
				observer.error(new Error('Socket no está conectado'));
				return;
			}

			this.socket.emit(eventName, data, (response: R) => {
				observer.next(response);
				observer.complete();
			});
		});
	}

	/**
	 * Une el socket a una sala específica
	 * @param roomId ID de la sala
	 */
	joinRoom(roomId: string): void {
		this.emit('joinRoom', { roomId });

		// Escuchar confirmación
		this.socket?.once('joinedRoom', (data) => {
			console.log('✅ Unido a la sala:', data.roomId);
		});
	}

	/**
	 * Sale de una sala específica
	 * @param roomId ID de la sala
	 */
	leaveRoom(roomId: string): void {
		this.emit('leaveRoom', { roomId });

		// Escuchar confirmación
		this.socket?.once('leftRoom', (data) => {
			console.log('✅ Saliste de la sala:', data.roomId);
		});
	}

	/**
	 * Envía un mensaje al servidor
	 * @param type Tipo de mensaje
	 * @param message Mensaje a enviar
	 * @param data Datos adicionales
	 * @param userId ID del usuario destinatario (opcional, si no se envía es broadcast)
	 */
	sendMessage(
		type: 'message' | 'notification' | 'alert' | 'update',
		message: string,
		data?: unknown,
		userId?: string
	): void {
		this.emit('sendMessage', {
			type,
			message,
			data,
			userId
		});
	}

	/**
	 * Obtiene el estado del servidor
	 * @returns Observable con el estado del servidor
	 */
	getStatus(): Observable<{ success: boolean; data: unknown }> {
		return this.emitWithAck('getStatus', {});
	}

	/**
	 * Envía un ping al servidor para mantener la conexión activa
	 */
	ping(): void {
		this.emit('ping');

		// Escuchar pong
		this.socket?.once('pong', (data) => {
			console.log('🏓 Pong recibido:', data.timestamp);
		});
	}
}
